import { useEffect, useState } from 'react';
import orchardService from '../../services/orchard';
import { getFirstDayOfMonthInTz, getTodayInTz, useOrgTimezone } from '../../utils/timezone';
import type { PlaceTarget } from '../../types/labour';

export interface PlaceOption {
  key: string;
  label: string;
  group: string;
  target: PlaceTarget;
}

/** Every block and cultivar area in the org, for pickers — "Farm › Block › Area". */
export function usePlaceOptions(): PlaceOption[] {
  const [opts, setOpts] = useState<PlaceOption[]>([]);
  useEffect(() => {
    let live = true;
    Promise.all([orchardService.getAllBlocks(), orchardService.getAllCultivarAreas()])
      .then(([b, a]) => {
        if (!live) return;
        const blocks = b.success ? b.data.blocks : [];
        const areas = a.success ? a.data.areas : [];
        const list: PlaceOption[] = [];
        for (const blk of blocks) {
          const group = blk.farm_name || 'Farm';
          list.push({ key: `block:${blk.id}`, label: `${blk.name} (whole block)`, group, target: { type: 'block', id: blk.id } });
          for (const ca of areas.filter(x => x.block_id === blk.id)) {
            list.push({ key: `cultivar_area:${ca.id}`, label: `${blk.name} › ${ca.name}`, group, target: { type: 'cultivar_area', id: ca.id } });
          }
        }
        setOpts(list);
      })
      .catch(() => { if (live) setOpts([]); });
    return () => { live = false; };
  }, []);
  return opts;
}

/** A from/to pair in the org's calendar, defaulting to this month so far. */
export function useDateRange() {
  const tz = useOrgTimezone();
  const [from, setFrom] = useState(() => getFirstDayOfMonthInTz(tz));
  const [to, setTo] = useState(() => getTodayInTz(tz));
  return { from, to, setFrom, setTo };
}

export function money(n: number | undefined | null, currency: string): string {
  if (n == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(n);
  } catch {
    return n.toFixed(2);
  }
}

export function hrs(n: number | undefined | null): string {
  if (n == null) return '—';
  return `${n.toFixed(n >= 100 ? 0 : 1)} h`;
}

export function unitsText(units: Record<string, number> | undefined): string {
  if (!units) return '';
  return Object.entries(units).map(([u, v]) => `${Math.round(v * 10) / 10} ${u}`).join(', ');
}
